/**
 * Application header component
 *
 * @author Claude
 */
import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Divider,
  ListItemIcon,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../../hooks/useAuth';
import { CompactRateLimitIndicator } from '../RateLimitIndicator';
import { GlobalSearch } from '../common/GlobalSearch';
import { ThemeSwitcher } from '../common/ThemeSwitcher';
import { LanguageSelector } from '../common/LanguageSelector';

interface HeaderProps {
  drawerWidth?: number;
  handleDrawerToggle?: () => void;
}

/**
 * Application header with navigation and user controls
 * @param {HeaderProps} props - Component props
 * @returns {JSX.Element} Header component
 *
 * @a11y Uses proper heading structure with logo as h1
 * @a11y Menu button has aria-label and expanded state
 * @a11y Navigation tabs have proper focus management
 */
export const Header = ({ drawerWidth = 240, handleDrawerToggle }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showSearchDialog, setShowSearchDialog] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isClientViewer = user?.role === 'client_viewer';

  // Build tab config based on user role
  const tabs = [
    { label: 'Dashboard', path: '/dashboard' },
    ...(isClientViewer
      ? []
      : [
          { label: 'Dimensions', path: '/dimensions' },
          { label: 'Compare', path: '/comparative-analysis' },
        ]),
  ];

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    const index = tabs.findIndex(t => path.startsWith(t.path));
    return index >= 0 ? index : false;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (tabs[newValue]) {
      navigate(tabs[newValue].path);
    }
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    setPendingPath(path);
    setAnchorEl(null);
  };

  const handleMenuExited = () => {
    if (pendingPath) {
      navigate(pendingPath);
      setPendingPath(null);
    }
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        height: { xs: 75, md: 75 },
        justifyContent: 'center',
      }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          {handleDrawerToggle && (
            <IconButton
              color="inherit"
              aria-label="open navigation menu"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo and App Title */}
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/dashboard"
            sx={{
              mr: 4,
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <BarChartIcon sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }} />
            <span>Accessibility Maturity Reporter</span>
          </Typography>

          {/* Main Navigation */}
          {user && !isMobile && (
            <Tabs
              value={getActiveTab()}
              onChange={handleTabChange}
              textColor="inherit"
              indicatorColor="secondary"
              aria-label="main navigation"
            >
              {tabs.map(tab => (
                <Tab key={tab.path} label={tab.label} />
              ))}
            </Tabs>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Global Search */}
          {!isMobile && (
            <Box sx={{ mr: 2, minWidth: 350, maxWidth: 500, flexGrow: 0.5 }}>
              <GlobalSearch variant="standard" />
            </Box>
          )}

          {/* Search Icon for Mobile */}
          {isMobile && (
            <IconButton
              onClick={() => setShowSearchDialog(true)}
              color="inherit"
              aria-label="open search"
              sx={{ mr: 1 }}
            >
              <SearchIcon />
            </IconButton>
          )}

          {/* Rate Limit Indicator */}
          <CompactRateLimitIndicator sx={{ mr: 2 }} />

          {/* User Menu */}
          {user ? (
            <Box sx={{ ml: 2 }}>
              <IconButton
                onClick={handleOpenUserMenu}
                aria-label="user account menu"
                aria-controls={isMenuOpen ? 'user-menu' : undefined}
                aria-expanded={isMenuOpen ? 'true' : undefined}
                aria-haspopup="true"
              >
                <Avatar sx={{ bgcolor: 'primary.dark' }}>
                  {user.firstName?.charAt(0) || <AccountCircleIcon />}
                </Avatar>
              </IconButton>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleCloseUserMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                disableScrollLock
                TransitionProps={{ onExited: handleMenuExited }}
              >
                <MenuItem disabled>
                  <Typography variant="body2">
                    {user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.email}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleNavigate('/dashboard')}>
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  Dashboard
                </MenuItem>
                {isMobile && !isClientViewer && (
                  <>
                    <MenuItem onClick={() => handleNavigate('/dimensions')}>
                      <ListItemIcon>
                        <BarChartIcon fontSize="small" />
                      </ListItemIcon>
                      Dimensions
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigate('/comparative-analysis')}>
                      <ListItemIcon>
                        <CompareArrowsIcon fontSize="small" />
                      </ListItemIcon>
                      Compare Reports
                    </MenuItem>
                  </>
                )}
                {!isClientViewer && (
                  <MenuItem onClick={() => handleNavigate('/admin')}>
                    <ListItemIcon>
                      <AdminPanelSettingsIcon fontSize="small" />
                    </ListItemIcon>
                    Admin
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={() => handleNavigate('/user/profile')}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('/user/settings')}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('/user/change-password')}>
                  <ListItemIcon>
                    <LockIcon fontSize="small" />
                  </ListItemIcon>
                  Change Password
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button component={Link} to="/login" color="inherit">
              Login
            </Button>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Search Dialog */}
      {showSearchDialog && (
        <GlobalSearch variant="dialog" onClose={() => setShowSearchDialog(false)} />
      )}
    </AppBar>
  );
};
